import React from "react";
import { useRouteMatch } from "react-router-dom";

import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import { Collapse, IconButton, Stack, Typography } from "@mui/material";
import { WorkspaceIcon } from "assets/material-custom-icons";
import { Routes } from "constants/routes";
import { IFolder } from "store/types";

import { useClientWorkspaceCtx } from "../context/ClientWorkspace-context";
import { VirtualizedFolderList } from "./VirtualizedFolderList";

const chevronColor = "#0A3142";
const iconColor = "#0A3142";
const labelColor = "#20293C";
const HEADER_HEIGHT = 36;

interface WorkspaceHeaderProps {
  isSelected: boolean;
  isWorkspaceFolderOpen: boolean;
  onWorkspaceArrowClick: () => void;
  onWorkspaceFolderClick: () => void;
  workspaceFolders: IFolder[];
}

const WorkspaceHeader: React.FC<WorkspaceHeaderProps> = ({
  isSelected,
  isWorkspaceFolderOpen,
  onWorkspaceArrowClick,
  onWorkspaceFolderClick,
  workspaceFolders
}) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        margin: "2px",
        height: "36px",
        padding: "0 5px",
        borderRadius: "20px",
        cursor: "pointer",
        backgroundColor: isSelected ? "#DBE8F5" : "transparent",
        "&:hover": {
          backgroundColor: "#ECF1F8"
        }
      }}
      gap="5px"
    >
      {workspaceFolders.length ? (
        <IconButton onClick={onWorkspaceArrowClick}>
          <ExpandMoreOutlinedIcon
            sx={{
              fill: chevronColor,
              fontSize: 20,
              transform: isWorkspaceFolderOpen ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.2s ease"
            }}
          />
        </IconButton>
      ) : (
        <Stack sx={{ width: 30, height: 20 }} />
      )}
      <Stack
        direction="row"
        alignItems="center"
        gap="5px"
        onClick={onWorkspaceFolderClick}
        sx={{
          flex: 1,
          minWidth: 0,
          cursor: "pointer"
        }}
      >
        <WorkspaceIcon sx={{ fill: iconColor, fontSize: 20 }} />
        <Typography
          sx={{
            color: labelColor,
            fontWeight: 700,
            fontSize: "16px"
          }}
        >
          Workspace
        </Typography>
      </Stack>
    </Stack>
  );
};

interface Props {
  availableHeight?: number;
}

export const Workspace: React.FC<Props> = ({ availableHeight = 0 }) => {
  const { onWorkspaceFolderClick, onWorkspaceArrowClick, isWorkspaceFolderOpen, folders } = useClientWorkspaceCtx();
  const workspaceRootMatch = useRouteMatch({ path: Routes.clientWorkspace, exact: true });
  const isSelected = !!workspaceRootMatch;

  return (
    <Stack
      sx={{
        width: "100%",
        flexDirection: "column",
        flexShrink: 0
      }}
    >
      <WorkspaceHeader
        isSelected={isSelected}
        isWorkspaceFolderOpen={isWorkspaceFolderOpen}
        onWorkspaceArrowClick={onWorkspaceArrowClick}
        onWorkspaceFolderClick={onWorkspaceFolderClick}
        workspaceFolders={folders}
      />
      <Collapse in={isWorkspaceFolderOpen} timeout={200} unmountOnExit>
        {/* Pass the full flat list so child lookups (getChildFolders) can resolve nested folders;
            VirtualizedFolderList derives the roots itself via getRootFolders. */}
        <VirtualizedFolderList maxHeight={Math.max(0, availableHeight - HEADER_HEIGHT)} folderList={folders} />
      </Collapse>
    </Stack>
  );
};
